import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { type LegacySiteReadingDoc } from '../../generics/LegacySiteReadingPrimitives.js'
import {
  legacyReadingSourceId,
  type LegacyReadingSourceId,
} from './LegacyReadingSource.js'
import { legacySiteId, type LegacySiteId } from './LegacySite.js'

/** Identifier type for public.legacy_site_reading_ */
export type LegacySiteReadingDocOid = string & {
  __brand: 'LegacySiteReadingDocOid'
}

/** Represents the table public.legacy_site_reading_ */
export default interface LegacySiteReadingTable {
  /** Database type: pg_catalog.int4 */
  siteId: ColumnType<LegacySiteId, LegacySiteId, LegacySiteId>

  /** Database type: pg_catalog.text */
  docOid: ColumnType<
    LegacySiteReadingDocOid,
    LegacySiteReadingDocOid,
    LegacySiteReadingDocOid
  >

  /** Database type: pg_catalog.timestamptz */
  tsEmail: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.timestamptz */
  tsRecord: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.text */
  imei: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  loggerSerial: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  legacyReadingSource: ColumnType<
    LegacyReadingSourceId,
    LegacyReadingSourceId,
    LegacyReadingSourceId
  >

  /** Database type: pg_catalog.jsonb */
  doc: ColumnType<
    LegacySiteReadingDoc,
    LegacySiteReadingDoc,
    LegacySiteReadingDoc
  >

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date, Date | string | undefined, Date | string>
}

export type LegacySiteReading = Selectable<LegacySiteReadingTable>

export type LegacySiteReadingInitializer = Insertable<LegacySiteReadingTable>

export type LegacySiteReadingMutator = Updateable<LegacySiteReadingTable>

export const legacySiteReadingDocOid =
  z.string() as unknown as z.Schema<LegacySiteReadingDocOid>

export const legacySiteReading = z.object({
  siteId: legacySiteId,
  docOid: legacySiteReadingDocOid,
  tsEmail: z.coerce.date().nullable().optional(),
  tsRecord: z.coerce.date().nullable().optional(),
  imei: z.string().nullable().optional(),
  loggerSerial: z.string().nullable().optional(),
  legacyReadingSource: legacyReadingSourceId,
  doc: z.unknown(),
  deletedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
}) as unknown as z.Schema<LegacySiteReading>

export const legacySiteReadingInitializer = z.object({
  siteId: legacySiteId,
  docOid: legacySiteReadingDocOid,
  tsEmail: z.coerce.date().optional().nullable(),
  tsRecord: z.coerce.date().optional().nullable(),
  imei: z.string().optional().nullable(),
  loggerSerial: z.string().optional().nullable(),
  legacyReadingSource: legacyReadingSourceId,
  doc: z.unknown(),
  deletedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
}) as unknown as z.Schema<LegacySiteReadingInitializer>

export const legacySiteReadingMutator = z.object({
  siteId: legacySiteId.optional(),
  docOid: legacySiteReadingDocOid.optional(),
  tsEmail: z.coerce.date().optional().nullable(),
  tsRecord: z.coerce.date().optional().nullable(),
  imei: z.string().optional().nullable(),
  loggerSerial: z.string().optional().nullable(),
  legacyReadingSource: legacyReadingSourceId.optional(),
  doc: z.unknown().optional(),
  deletedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
}) as unknown as z.Schema<LegacySiteReadingMutator>
